import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOBreadcrumbs from '../components/SEOBreadcrumbs';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../hooks/usePageSEO';
import {
  Visibility,
  ImageSearch,
  Restaurant,
  TrendingUp,
  Timeline,
  VerifiedUser,
  MenuBook,
  TuneOutlined,
  Warning,
  Science,
  ExpandMore,
  ExpandLess,
  Camera,
  Calculate,
  CheckCircle,
  Cancel
} from '../mui-icons';
import './NutritionPage.css';

const NutritionPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expandedSection, setExpandedSection] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const sectionRefs = useRef({});
  
  // SEO dinâmico para esta página
  usePageSEO();

  // Lista de seções na ordem
  const sections = [
    { id: 'overview', icon: <Visibility />, color: '#2196F3' },
    { id: 'imageAnalysis', icon: <ImageSearch />, color: '#9C27B0' },
    { id: 'nutritionalData', icon: <Restaurant />, color: '#FF9800' },
    { id: 'healthScore', icon: <TrendingUp />, color: '#4CAF50' },
    { id: 'interpretation', icon: <Timeline />, color: '#00BCD4' },
    { id: 'compliance', icon: <VerifiedUser />, color: '#3F51B5' },
    { id: 'userGuide', icon: <MenuBook />, color: '#E91E63' },
    { id: 'factors', icon: <TuneOutlined />, color: '#607D8B' },
    { id: 'limitations', icon: <Warning />, color: '#FF5722' },
    { id: 'references', icon: <Science />, color: '#009688' }
  ];

  // Scroll to section and expand
  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setExpandedSection(sectionId);
      setActiveSection(sectionId);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // Intersection Observer para destacar seção ativa no menu
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.dataset.section);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Renderizar seção Overview
  const renderOverviewSection = () => {
    const data = t('nutrition.sections.overview');
    return (
      <div className="section-content">
        <p className="section-intro">{data.intro}</p>
        <h3 className="subsection-title">{data.process.title}</h3>
        <div className="process-steps">
          {data.process.steps.map((step, index) => (
            <div key={index} className="process-step">
              <div className="step-icon-wrapper">
                {index === 0 && <Camera sx={{ fontSize: 32 }} />}
                {index === 1 && <ImageSearch sx={{ fontSize: 32 }} />}
                {index === 2 && <Calculate sx={{ fontSize: 32 }} />}
                {index === 3 && <CheckCircle sx={{ fontSize: 32 }} />}
              </div>
              <h4 className="step-title">{step.title}</h4>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Renderizar seção Image Analysis
  const renderImageAnalysisSection = () => {
    const data = t('nutrition.sections.imageAnalysis');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="info-block">
          <h3 className="subsection-title">{data.capture.title}</h3>
          <ul className="info-list">
            {data.capture.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.identification.title}</h3>
          <p className="subsection-description">{data.identification.description}</p>
          <ul className="info-list">
            {data.identification.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.technology.title}</h3>
          <p className="subsection-description">{data.technology.description}</p>
        </div>
      </div>
    );
  };

  // Renderizar seção Nutritional Data
  const renderNutritionalDataSection = () => {
    const data = t('nutrition.sections.nutritionalData');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="info-block">
          <h3 className="subsection-title">{data.calculation.title}</h3>
          <div className="calculation-steps">
            {data.calculation.steps.map((step, index) => (
              <div key={index} className="calc-step">
                <h4 className="calc-step-title">{step.title}</h4>
                <p className="calc-step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.macros.title}</h3>
          <p className="subsection-description">{data.macros.description}</p>
          <ul className="info-list">
            {data.macros.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.sources.title}</h3>
          <ul className="info-list">
            {data.sources.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Renderizar seção Health Score
  const renderHealthScoreSection = () => {
    const data = t('nutrition.sections.healthScore');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="info-block">
          <h3 className="subsection-title">{data.methodology.title}</h3>
          <p className="subsection-description">{data.methodology.description}</p>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.factors.title}</h3>
          <div className="factors-grid">
            {data.factors.items.map((factor, index) => (
              <div key={index} className="factor-card">
                <div className="factor-header">
                  <h4 className="factor-title">{factor.title}</h4>
                  <span className="factor-points">{factor.points}</span>
                </div>
                <p className="factor-description">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Renderizar seção Interpretation
  const renderInterpretationSection = () => {
    const data = t('nutrition.sections.interpretation');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="scale-container">
          {data.scale.map((item, index) => (
            <div key={index} className="scale-item" style={{ borderLeft: `4px solid ${item.color}` }}>
              <div className="scale-header">
                <span className="scale-range">{item.range}</span>
                <span className="scale-label" style={{ color: item.color }}>{item.label}</span>
              </div>
              <p className="scale-characteristics">{item.characteristics}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Renderizar seção Compliance
  const renderComplianceSection = () => {
    const data = t('nutrition.sections.compliance');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="compliance-grid">
          <div className="compliance-card">
            <h3 className="compliance-title">{data.apple.title}</h3>
            <ul className="compliance-list">
              {data.apple.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          
          <div className="compliance-card">
            <h3 className="compliance-title">{data.google.title}</h3>
            <ul className="compliance-list">
              {data.google.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar seção User Guide
  const renderUserGuideSection = () => {
    const data = t('nutrition.sections.userGuide');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="info-block">
          <h3 className="subsection-title">{data.interpretation.title}</h3>
          <p className="subsection-description">{data.interpretation.description}</p>
        </div>

        <div className="info-block">
          <h3 className="subsection-title">{data.improve.title}</h3>
          <ul className="info-list">
            {data.improve.items.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Renderizar seção Factors
  const renderFactorsSection = () => {
    const data = t('nutrition.sections.factors');
    return (
      <div className="section-content">
        <div className="factors-comparison">
          <div className="factors-column increase">
            <div className="factors-column-header">
              <CheckCircle sx={{ fontSize: 32 }} />
              <h3>{data.increase.title}</h3>
            </div>
            <ul className="factors-list">
              {data.increase.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="factors-column decrease">
            <div className="factors-column-header">
              <Cancel sx={{ fontSize: 32 }} />
              <h3>{data.decrease.title}</h3>
            </div>
            <ul className="factors-list">
              {data.decrease.items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar seção Limitations
  const renderLimitationsSection = () => {
    const data = t('nutrition.sections.limitations');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="limitations-grid">
          {data.items.map((item, index) => (
            <div key={index} className="limitation-card">
              <h4 className="limitation-title">{item.title}</h4>
              <p className="limitation-description">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="disclaimer-box">
          <Warning sx={{ fontSize: 24, color: '#FF9800' }} />
          <p className="disclaimer-text">{data.disclaimer}</p>
        </div>
      </div>
    );
  };

  // Renderizar seção References
  const renderReferencesSection = () => {
    const data = t('nutrition.sections.references');
    return (
      <div className="section-content">
        <p className="section-subtitle-text">{data.subtitle}</p>
        
        <div className="references-container">
          {data.categories.map((category, index) => (
            <div key={index} className="reference-category">
              <h3 className="reference-category-title">{category.title}</h3>
              <ul className="reference-list">
                {category.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Mapeamento das funções de renderização
  const sectionRenderers = {
    overview: renderOverviewSection,
    imageAnalysis: renderImageAnalysisSection,
    nutritionalData: renderNutritionalDataSection,
    healthScore: renderHealthScoreSection,
    interpretation: renderInterpretationSection,
    compliance: renderComplianceSection,
    userGuide: renderUserGuideSection,
    factors: renderFactorsSection,
    limitations: renderLimitationsSection,
    references: renderReferencesSection
  };

  return (
    <div className="nutrition-page surface-marketing">
      <Header />
      <SEOBreadcrumbs />

      {/* Hero Section */}
      <section className="nutrition-hero">
        <div className="container">
          <h1 className="nutrition-hero-title">{t('nutrition.hero.title')}</h1>
          <p className="nutrition-hero-subtitle">{t('nutrition.hero.subtitle')}</p>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <div className="nutrition-layout">
        <div className="container nutrition-container">
          {/* Sidebar Navigation */}
          <aside className="nutrition-sidebar">
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                  style={{
                    '--section-color': section.color
                  }}
                >
                  <div className="sidebar-icon" style={{ color: section.color }}>
                    {section.icon}
                  </div>
                  <span className="sidebar-label">{t(`nutrition.menu.${section.id}`)}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Content Area */}
          <main className="nutrition-content">
            {sections.map((section) => (
              <div
                key={section.id}
                ref={(el) => (sectionRefs.current[section.id] = el)}
                data-section={section.id}
                className="section-wrapper"
              >
                <div
                  className={`section-card ${expandedSection === section.id ? 'expanded' : ''}`}
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="section-header">
                    <div className="section-header-left">
                      <div className="section-icon" style={{ backgroundColor: section.color }}>
                        {section.icon}
                      </div>
                      <h2 className="section-title">
                        {t(`nutrition.sections.${section.id}.title`)}
                      </h2>
                    </div>
                    <div className="section-toggle">
                      {expandedSection === section.id ? (
                        <ExpandLess sx={{ fontSize: 32 }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 32 }} />
                      )}
                    </div>
                  </div>

                  {expandedSection === section.id && (
                    <div className="section-body" onClick={(e) => e.stopPropagation()}>
                      {sectionRenderers[section.id]()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default NutritionPage;

